import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useHouseholdMembership } from '../contexts/HouseholdMembershipContext';
import { useSampleData } from '../contexts/SampleDataContext';
import { getAnalyticsTrends } from '../services/analyticsService';
import {
  getDashboardRefreshGeneration,
  subscribeDashboardRefresh,
} from '../services/dashboardRefreshBus';
import type { AnalyticsTrendPoint } from '../types/analytics';
import { shiftMonthKey } from './useSpendBreakdown';

export type SpendCategoryTab = {
  id: string;
  name: string;
  color: string;
};

type Options = {
  /** Category slug, or null / 'all' for total spend. */
  categoryId: string | null;
  /** How many recent months to show. */
  limit?: number;
  enabled?: boolean;
};

function currentMonthKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Oldest → newest month keys ending at the current UTC month. */
export function recentMonthKeys(count: number, endKey = currentMonthKey()): string[] {
  const keys: string[] = [];
  for (let i = count - 1; i >= 0; i--) {
    keys.push(shiftMonthKey(endKey, -i));
  }
  return keys;
}

/** Prefer a clean local month window so duplicate/skipped server keys don't show twice. */
function alignTrendPoints(
  points: AnalyticsTrendPoint[],
  limit: number,
): AnalyticsTrendPoint[] {
  const byKey = new Map(points.map((p) => [p.periodKey, p]));
  return recentMonthKeys(limit).map((periodKey) => {
    const hit = byKey.get(periodKey);
    return {
      periodKey,
      totalAmount: hit?.totalAmount ?? 0,
      transactionCount: hit?.transactionCount ?? 0,
    };
  });
}

export function useSpendCategoryTrends(options: Options) {
  const { isAuthenticated, tokens } = useAuth();
  const { isSampleMode } = useSampleData();
  const { view: householdView, viewReady } = useHouseholdMembership();
  const limit = options.limit ?? 6;
  const enabled = options.enabled !== false;
  const categorySlug =
    options.categoryId && options.categoryId !== 'all' ? options.categoryId : undefined;

  const [points, setPoints] = useState<AnalyticsTrendPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const seenGenerationRef = useRef(0);
  const hasDataRef = useRef(false);
  hasDataRef.current = points.some((p) => p.totalAmount !== 0 || p.transactionCount > 0);

  const useLive = enabled && isAuthenticated && !isSampleMode && !!tokens?.accessToken;

  useEffect(() => {
    const applyGeneration = (generation: number) => {
      if (generation === seenGenerationRef.current) return;
      seenGenerationRef.current = generation;
      setTick((v) => v + 1);
    };
    applyGeneration(getDashboardRefreshGeneration());
    return subscribeDashboardRefresh(applyGeneration);
  }, []);

  useEffect(() => {
    if (!useLive || !tokens?.accessToken || !viewReady) {
      if (!useLive || !tokens?.accessToken) {
        setPoints([]);
        setError(null);
        setLoading(false);
      }
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void getAnalyticsTrends({
      accessToken: tokens.accessToken,
      period: 'month',
      limit,
      categorySlug,
      view: householdView,
      // Bust caches whenever category / window / refresh tick changes.
      forceRefresh: true,
    })
      .then((response) => {
        if (cancelled) return;
        const raw = Array.isArray(response.points) ? response.points : [];
        setPoints(alignTrendPoints(raw, limit));
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        if (!hasDataRef.current) setPoints([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [useLive, tokens?.accessToken, categorySlug, limit, tick, householdView, viewReady]);

  return {
    points,
    loading,
    error,
    isLive: useLive,
    refresh: () => setTick((v) => v + 1),
  };
}
